import { evidenceFileError, evidenceFileType } from '../evidence/evidenceModel.ts';

export const ODOMETER_FILE_ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

export function captureFileError(file: Pick<File, 'name' | 'type' | 'size'>, kind: 'receipt' | 'odometer' | 'expense'): string | null {
  const error = evidenceFileError(file);
  if (error === 'native.capture.fileSize') return error;
  if (kind === 'odometer' && (error || evidenceFileType(file) === 'application/pdf')) return 'costs.capture.imageFileType';
  return error;
}

export function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('native.capture.imageFailed'));
    reader.readAsDataURL(file);
  });
}

/** Camera and uploaded odometers share the same bounded JPEG transport. */
export async function scaledCaptureImage(source: CanvasImageSource, width: number, height: number): Promise<string> {
  const scale = Math.min(1, 1800 / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  try {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('native.capture.imageFailed');
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
    if (!blob) throw new Error('native.capture.imageFailed');
    const data = await fileToDataUrl(blob);
    // The native journey journal limits the complete data URL, not raw bytes.
    if (data.length > 279_876_268) throw new Error('native.capture.fileSize');
    return data;
  } finally {
    canvas.width = 0; canvas.height = 0;
  }
}

export async function readCaptureFile(file: File, kind: 'receipt' | 'odometer' | 'expense'): Promise<string> {
  const invalid = captureFileError(file, kind);
  if (invalid) throw new Error(invalid);
  const blob = new Blob([file], { type: evidenceFileType(file) });
  // Receipt originals, including PDFs, retain their existing file workflow.
  if (kind !== 'odometer') return fileToDataUrl(blob);
  return normalizeOdometerBlob(blob);
}

export async function normalizeOdometerDataUrl(dataUrl: string): Promise<string> {
  if (!/^data:image\/(jpeg|png|webp);base64,/.test(dataUrl)) throw new Error('costs.capture.imageFileType');
  const bytes = Uint8Array.from(atob(dataUrl.slice(dataUrl.indexOf(',') + 1)), char => char.charCodeAt(0));
  return normalizeOdometerBlob(new Blob([bytes], { type: dataUrl.slice(5, dataUrl.indexOf(';')) }));
}

async function normalizeOdometerBlob(blob: Blob): Promise<string> {
  const url = URL.createObjectURL(blob);
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('native.capture.imageFailed'));
      image.src = url;
    });
    const { naturalWidth: width, naturalHeight: height } = image;
    if (!width || !height || width * height > 16_000_000 || width > 10_000 || height > 10_000) {
      throw new Error('native.capture.imageDimensions');
    }
    // Re-encoding strips file metadata and matches live photos, including
    // the existing offline/native journal and mileage-analysis contracts.
    return await scaledCaptureImage(image, width, height);
  } finally {
    image.onload = null; image.onerror = null; image.src = '';
    URL.revokeObjectURL(url);
  }
}
